import { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import styles from './EnrollmentsPage.module.css';

interface Student {
  id: string;
  firstName: string;
  paternalLastName: string;
  curp?: string;
}

interface Fee {
  id: string;
  name: string;
  amount: number;
  isActive: boolean;
}

interface Grade {
  id: string;
  name: string;
  groups: { id: string; name: string }[];
}

interface Enrollment {
  id: string;
  studentId: string;
  groupId: string;
  year: string;
  status: string;
  hasScholarship: number;
  scholarshipPercentage: number;
  createdAt: string;
  student: { id: string; firstName: string; paternalLastName: string } | null;
  group: { id: string; name: string; grade: { id: string; name: string } | null } | null;
}

interface SchoolData {
  settings: string;
  stats?: Record<string, number>;
}

interface StudentFee {
  id: string;
  studentId: string;
  feeId: string;
  enrollmentId: string;
  amount: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  fee: { name: string; amount: number } | null;
}

const currentYear = new Date().getFullYear();

function parseSettings(raw: string): Record<string, any> {
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function schoolCycleLabel(settings: Record<string, any>): string | null {
  const start = settings.schoolYearStart;
  if (!start) return null;
  const y = new Date(start).getFullYear();
  if (isNaN(y)) return null;
  return `${y}-${y + 1}`;
}

export default function EnrollmentsPage() {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [fees, setFees] = useState<Fee[]>([]);
  const [tuitionCost, setTuitionCost] = useState(0);
  const [schoolSettings, setSchoolSettings] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const cycleFromSettings = schoolCycleLabel(schoolSettings);
  const yearOptions = [
    ...new Set([
      ...(cycleFromSettings ? [cycleFromSettings] : []),
      `${currentYear}-${currentYear + 1}`,
      `${currentYear + 1}-${currentYear + 2}`,
      `${currentYear + 2}-${currentYear + 3}`,
    ]),
  ];

  // Update form year when settings load with the school cycle
  useEffect(() => {
    if (cycleFromSettings && !form.studentId) {
      setForm((prev) => ({ ...prev, year: cycleFromSettings }));
    }
  }, [cycleFromSettings]);

  const [form, setForm] = useState({
    studentId: '',
    groupId: '',
    year: `${currentYear}-${currentYear + 1}`,
    status: 'ACTIVE',
    hasScholarship: false,
    scholarshipPercentage: 0,
    selectedFeeIds: [] as string[],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [studentFeesMap, setStudentFeesMap] = useState<Record<string, StudentFee[]>>({});
  const [editingEnrollment, setEditingEnrollment] = useState<Enrollment | null>(null);
  const [editFeeIds, setEditFeeIds] = useState<string[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  // Calculated total
  const discount = form.hasScholarship ? (tuitionCost * form.scholarshipPercentage) / 100 : 0;
  const totalAfterScholarship = tuitionCost - discount;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [enrollmentsData, studentsData, gradesData, schoolData, feesData] = await Promise.all([
        api.get<Enrollment[]>('/api/enrollments'),
        api.get<Student[]>('/api/students'),
        api.get<Grade[]>('/api/school/grades'),
        api.get<SchoolData>('/api/school'),
        api.get<Fee[]>('/api/fees'),
      ]);
      setEnrollments(Array.isArray(enrollmentsData) ? enrollmentsData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : []);
      setGrades(Array.isArray(gradesData) ? gradesData : []);
      setFees(Array.isArray(feesData) ? feesData.filter((f: Fee) => f.isActive) : []);

      // Load student fees per enrollment
      const enrollmentsList = Array.isArray(enrollmentsData) ? enrollmentsData : [];
      const feesMap: Record<string, StudentFee[]> = {};
      await Promise.all(
        enrollmentsList.map(async (enr: Enrollment) => {
          try {
            const sfs = await api.get<StudentFee[]>(
              `/api/fees/student-fees/by-enrollment?enrollmentId=${enr.id}`,
            );
            if (Array.isArray(sfs)) feesMap[enr.id] = sfs;
          } catch {
            /* no fees for this enrollment */
          }
        }),
      );
      setStudentFeesMap(feesMap);

      // Extract tuition cost from school settings (JSON string)
      if (schoolData?.settings) {
        try {
          const settings = JSON.parse(schoolData.settings);
          setSchoolSettings(settings);
          setTuitionCost(parseFloat(settings.tuitionCost) || 0);
        } catch {
          setTuitionCost(0);
        }
      }
    } catch {
      setError('Error al cargar datos');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.studentId || !form.groupId) {
      setError('Seleccioná un alumno y un grupo');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const enrollment = await api.post<any>('/api/enrollments', {
        studentId: form.studentId,
        groupId: form.groupId,
        year: form.year,
        status: form.status,
        hasScholarship: form.hasScholarship,
        scholarshipPercentage: form.scholarshipPercentage,
      });

      // Create student_fee records for selected fee concepts
      const enrollmentId = enrollment?.id;
      if (enrollmentId && form.selectedFeeIds.length > 0) {
        await Promise.all(
          form.selectedFeeIds.map((feeId) =>
            api.post('/api/fees/student-fees', {
              studentId: form.studentId,
              feeId,
              enrollmentId,
            }),
          ),
        );
      }

      setShowModal(false);
      setForm({
        studentId: '',
        groupId: '',
        year: cycleFromSettings || `${currentYear}-${currentYear + 1}`,
        status: 'ACTIVE',
        hasScholarship: false,
        scholarshipPercentage: 0,
        selectedFeeIds: [],
      });
      fetchData();
    } catch (err: any) {
      setError(err?.message || 'Error al inscribir');
    }
    setSaving(false);
  };

  const openEditModal = (enrollment: Enrollment) => {
    const currentFees = studentFeesMap[enrollment.id] || [];
    setEditingEnrollment(enrollment);
    setEditFeeIds(currentFees.map((sf) => sf.feeId));
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEnrollment) return;
    setEditSaving(true);
    setError('');
    try {
      const enrollmentId = editingEnrollment.id;
      const currentFees = studentFeesMap[enrollmentId] || [];

      // Remove fees that were unchecked
      const feesToRemove = currentFees.filter((sf) => !editFeeIds.includes(sf.feeId));
      await Promise.all(feesToRemove.map((sf) => api.delete(`/api/fees/student-fees/${sf.id}`)));

      // Add fees that were newly checked
      const currentFeeIds = currentFees.map((sf) => sf.feeId);
      const feesToAdd = editFeeIds.filter((fid) => !currentFeeIds.includes(fid));
      await Promise.all(
        feesToAdd.map((feeId) =>
          api.post('/api/fees/student-fees', {
            studentId: editingEnrollment.studentId,
            feeId,
            enrollmentId,
          }),
        ),
      );

      setEditingEnrollment(null);
      setEditFeeIds([]);
      fetchData();
    } catch (err: any) {
      setError(err?.message || 'Error al actualizar conceptos');
    }
    setEditSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar esta inscripción?')) return;
    try {
      await api.delete(`/api/enrollments/${id}`);
      fetchData();
    } catch {
      alert('Error al eliminar inscripción');
    }
  };

  const statusLabel: Record<string, string> = {
    ACTIVE: 'Activo',
    WITHDRAWN: 'Retirado',
    GRADUATED: 'Egresado',
  };

  const statusColor: Record<string, string> = {
    ACTIVE: '#e8f5e9',
    WITHDRAWN: '#fff3e0',
    GRADUATED: '#e3f2fd',
  };

  const statusTextColor: Record<string, string> = {
    ACTIVE: '#2e7d32',
    WITHDRAWN: '#e65100',
    GRADUATED: '#1565c0',
  };

  // Filter students that are not already enrolled (ACTIVE)
  const activeStudentIds = new Set(
    enrollments.filter((e) => e.status === 'ACTIVE').map((e) => e.studentId),
  );
  const availableStudents = students.filter((s) => !activeStudentIds.has(s.id));

  const activeEnrollments = enrollments.filter((e) => e.status === 'ACTIVE');
  const byGrade = [...new Set(enrollments.map((e) => e.group?.grade?.name).filter(Boolean))];

  const formatMoney = (amount: number) =>
    `$${amount.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Inscripciones</h1>
        <button className={styles.addButton} onClick={() => setShowModal(true)}>
          + Nueva Inscripción
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <h3>Totales</h3>
          <p>{enrollments.length}</p>
        </div>
        <div className={styles.statCard}>
          <h3>Activos</h3>
          <p>{activeEnrollments.length}</p>
        </div>
        <div className={styles.statCard}>
          <h3>Colegiatura</h3>
          <p style={{ fontSize: '1.5rem' }}>{formatMoney(tuitionCost)}</p>
        </div>
        {byGrade.map((grade) => (
          <div className={styles.statCard} key={grade}>
            <h3>{grade}</h3>
            <p>
              {
                enrollments.filter((e) => e.group?.grade?.name === grade && e.status === 'ACTIVE')
                  .length
              }
            </p>
          </div>
        ))}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Alumno</th>
              <th>Grado</th>
              <th>Grupo</th>
              <th>Ciclo</th>
              <th>Beca</th>
              <th>Colegiatura</th>
              <th>Conceptos</th>
              <th>Estatus</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>
                  Cargando...
                </td>
              </tr>
            ) : enrollments.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: 20 }}>
                  Sin inscripciones registradas
                </td>
              </tr>
            ) : (
              enrollments.map((en) => {
                const enDiscount = en.hasScholarship
                  ? (tuitionCost * en.scholarshipPercentage) / 100
                  : 0;
                const enTotal = tuitionCost - enDiscount;

                return (
                  <tr key={en.id}>
                    <td>{en.student ? `${en.student.paternalLastName}, ${en.student.firstName}` : '—'}</td>
                    <td>{en.group?.grade?.name || '—'}</td>
                    <td>{en.group?.name || '—'}</td>
                    <td>{en.year || '—'}</td>
                    <td>
                      {en.hasScholarship ? (
                        <span className={styles.scholarshipBadge}>{en.scholarshipPercentage}%</span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{formatMoney(enTotal)}</td>
                    <td>
                      {(studentFeesMap[en.id] || []).length > 0 ? (
                        <div className={styles.feeList}>
                          {(studentFeesMap[en.id] || []).map((sf) => (
                            <span key={sf.id} className={styles.feeBadge}>
                              {sf.fee?.name || 'Concepto'}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#bbb', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{
                          background: statusColor[en.status] || '#f5f5f5',
                          color: statusTextColor[en.status] || '#333',
                        }}
                      >
                        {statusLabel[en.status] || en.status}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button className={styles.editBtn} onClick={() => openEditModal(en)}>
                          Editar
                        </button>
                        <button className={styles.deleteBtn} onClick={() => handleDelete(en.id)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={styles.overlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Nueva Inscripción</h2>
              <button className={styles.modalClose} onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreate} className={styles.form}>
              <div className={styles.field}>
                <label>Alumno</label>
                <select
                  value={form.studentId}
                  onChange={(e) => setForm({ ...form, studentId: e.target.value })}
                  required
                >
                  <option value="">Seleccionar alumno...</option>
                  {availableStudents.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.paternalLastName}, {s.firstName}
                      {s.curp ? ` (${s.curp})` : ''}
                    </option>
                  ))}
                </select>
                {availableStudents.length === 0 && students.length > 0 && (
                  <p className={styles.hint}>Todos los alumnos ya están inscritos activamente</p>
                )}
              </div>

              <div className={styles.field}>
                <label>Grupo</label>
                <select
                  value={form.groupId}
                  onChange={(e) => setForm({ ...form, groupId: e.target.value })}
                  required
                >
                  <option value="">Seleccionar grupo...</option>
                  {grades.map((g) => (
                    <optgroup key={g.id} label={g.name}>
                      {g.groups.map((gr) => (
                        <option key={gr.id} value={gr.id}>
                          {gr.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                {grades.length === 0 && (
                  <p className={styles.hint}>Primero creá grados y grupos en Configuración</p>
                )}
              </div>

              <div className={styles.field}>
                <label>Ciclo Escolar</label>
                <select
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label>Estatus</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  <option value="ACTIVE">Activo</option>
                  <option value="WITHDRAWN">Retirado</option>
                  <option value="GRADUATED">Egresado</option>
                </select>
              </div>

              {/* Scholarship section */}
              <div className={styles.scholarshipSection}>
                <div className={styles.scholarshipToggle}>
                  <label className={styles.toggleLabel}>
                    <input
                      type="checkbox"
                      checked={form.hasScholarship}
                      onChange={(e) => setForm({ ...form, hasScholarship: e.target.checked })}
                    />
                    <span className={styles.toggleCheckmark} />
                    ¿Aplica beca?
                  </label>
                </div>

                {form.hasScholarship && (
                  <div className={styles.field}>
                    <label>Porcentaje de beca (%)</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={form.scholarshipPercentage || ''}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          scholarshipPercentage: Math.min(
                            100,
                            Math.max(0, parseInt(e.target.value) || 0),
                          ),
                        })
                      }
                      placeholder="Ej: 50"
                      className={styles.input}
                    />
                  </div>
                )}
              </div>

              {/* Tuition summary */}
              {tuitionCost > 0 && (
                <div className={styles.tuitionSummary}>
                  <div className={styles.tuitionRow}>
                    <span>Colegiatura base:</span>
                    <span>{formatMoney(tuitionCost)}</span>
                  </div>
                  {form.hasScholarship && form.scholarshipPercentage > 0 && (
                    <>
                      <div className={styles.tuitionRow}>
                        <span>Descuento ({form.scholarshipPercentage}%):</span>
                        <span style={{ color: '#2e7d32' }}>-{formatMoney(discount)}</span>
                      </div>
                      <div className={styles.tuitionDivider} />
                      <div className={`${styles.tuitionRow} ${styles.tuitionTotal}`}>
                        <span>Total a pagar:</span>
                        <span>{formatMoney(totalAfterScholarship)}</span>
                      </div>
                    </>
                  )}
                  {(!form.hasScholarship || form.scholarshipPercentage === 0) && (
                    <>
                      <div className={styles.tuitionDivider} />
                      <div className={`${styles.tuitionRow} ${styles.tuitionTotal}`}>
                        <span>Total a pagar:</span>
                        <span>{formatMoney(tuitionCost)}</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {tuitionCost === 0 && (
                <p className={styles.hint} style={{ textAlign: 'center' }}>
                  ⚠️ No hay costo de colegiatura configurado. Configuralo en Ajustes → Configuración.
                </p>
              )}

              {/* Fee concepts selection */}
              {fees.length > 0 && (
                <div className={styles.scholarshipSection}>
                  <label
                    style={{
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      color: '#666',
                      display: 'block',
                      marginBottom: 8,
                    }}
                  >
                    Conceptos adicionales a pagar (además de colegiatura)
                  </label>
                  <div className={styles.feeGrid}>
                    {fees.map((f) => (
                      <label key={f.id} className={styles.feeChip}>
                        <input
                          type="checkbox"
                          checked={form.selectedFeeIds.includes(f.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm({ ...form, selectedFeeIds: [...form.selectedFeeIds, f.id] });
                            } else {
                              setForm({
                                ...form,
                                selectedFeeIds: form.selectedFeeIds.filter((id) => id !== f.id),
                              });
                            }
                          }}
                        />
                        <span>
                          {f.name} — ${f.amount.toFixed(2)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className={styles.cancelBtn}
                >
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className={styles.submitBtn}>
                  {saving ? 'Inscribiendo...' : 'Inscribir Alumno'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit enrollment modal */}
      {editingEnrollment && (
        <div className={styles.overlay} onClick={() => setEditingEnrollment(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Editar Inscripción</h2>
              <button className={styles.modalClose} onClick={() => setEditingEnrollment(null)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleEdit} className={styles.form}>
              {/* Enrollment info (read-only) */}
              <div className={styles.editInfoGrid}>
                <div className={styles.editInfoItem}>
                  <span className={styles.editInfoLabel}>Alumno</span>
                  <span className={styles.editInfoValue}>
                    {editingEnrollment.student
                      ? `${editingEnrollment.student.paternalLastName}, ${editingEnrollment.student.firstName}`
                      : '—'}
                  </span>
                </div>
                <div className={styles.editInfoItem}>
                  <span className={styles.editInfoLabel}>Grupo</span>
                  <span className={styles.editInfoValue}>
                    {editingEnrollment.group?.name || '—'}
                  </span>
                </div>
                <div className={styles.editInfoItem}>
                  <span className={styles.editInfoLabel}>Ciclo</span>
                  <span className={styles.editInfoValue}>{editingEnrollment.year || '—'}</span>
                </div>
                <div className={styles.editInfoItem}>
                  <span className={styles.editInfoLabel}>Estatus</span>
                  <span className={styles.editInfoValue}>
                    <span
                      className={styles.statusBadge}
                      style={{
                        background: statusColor[editingEnrollment.status] || '#f5f5f5',
                        color: statusTextColor[editingEnrollment.status] || '#333',
                      }}
                    >
                      {statusLabel[editingEnrollment.status] || editingEnrollment.status}
                    </span>
                  </span>
                </div>
              </div>

              {/* Fee concept management */}
              <div className={styles.scholarshipSection}>
                <label
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#666',
                    display: 'block',
                    marginBottom: 8,
                  }}
                >
                  Conceptos adicionales contratados
                </label>
                <p className={styles.hint} style={{ marginBottom: 12 }}>
                  Desactivá un concepto para darlo de baja (ej: si el alumno ya no lo cursa). Activá
                  uno nuevo si necesita agregarlo.
                </p>
                <div className={styles.feeGrid}>
                  {fees.map((f) => {
                    const isChecked = editFeeIds.includes(f.id);
                    return (
                      <label
                        key={f.id}
                        className={`${styles.feeChip} ${isChecked ? styles.feeChipActive : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            setEditFeeIds((prev) =>
                              prev.includes(f.id)
                                ? prev.filter((id) => id !== f.id)
                                : [...prev, f.id],
                            );
                          }}
                        />
                        <span>
                          {f.name} — ${f.amount.toFixed(2)}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => setEditingEnrollment(null)}
                  className={styles.cancelBtn}
                >
                  Cancelar
                </button>
                <button type="submit" disabled={editSaving} className={styles.submitBtn}>
                  {editSaving ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
